import { Vector3 } from 'three';

import type {
	CelestialPosition,
	GeoLightingState,
	GeoLocation,
	TimePreset,
	WeatherPreset
} from '../../types';
import type { GeoLightingManagerConfig, OriginalLightState } from './types';
import { DEFAULT_LIGHT_DISTANCE } from './helpers';
import {
	getCurrentTimeCfn,
	calculateSunPositionCfn,
	calculateMoonPositionCfn,
	storeOriginalLightStateCfn,
	restoreOriginalLightStateCfn,
	updateLightingCfn,
	startRealtimeUpdatesCfn,
	stopRealtimeUpdatesCfn
} from './functions';

/**
 * GeoLightingManager handles geographic-based lighting:
 * - Sun position calculation based on location, date, and time
 * - Moon position calculation for nighttime
 * - Weather-based lighting presets
 * - Time presets (sunrise, morning, afternoon, etc.)
 *
 * @example
 * ```typescript
 * const geoLighting = new GeoLightingManager({
 *   scene: scene,
 *   ambientLight: existingAmbientLight,
 *   directionalLight: existingDirectionalLight,
 *   hemisphereLight: existingHemisphereLight
 * });
 *
 * // Enable with location
 * geoLighting.enable({ latitude: 40.7128, longitude: -74.0060 });
 *
 * // Set time preset
 * geoLighting.setTime('golden-hour');
 *
 * // Set weather
 * geoLighting.setWeather('clear-sunny');
 *
 * // Disable
 * geoLighting.disable();
 * ```
 */
export class GeoLightingManager {
	#config: GeoLightingManagerConfig;
	#enabled = false;
	#location: GeoLocation = { latitude: 0, longitude: 0 };
	#time: Date | TimePreset | 'realtime' = 'afternoon';
	#weather: WeatherPreset = 'clear-sunny';
	#sceneCenter: Vector3;
	#lightDistance: number;

	// Realtime update interval
	#realtimeInterval: ReturnType<typeof setInterval> | null = null;

	// Stored original light values (to restore when disabled)
	#originalLightState: OriginalLightState = {};

	constructor(config: GeoLightingManagerConfig) {
		this.#config = config;
		this.#sceneCenter = config.sceneCenter ?? new Vector3(0, 0, 0);
		this.#lightDistance = config.lightDistance ?? DEFAULT_LIGHT_DISTANCE;
	}

	// ============ Getters ==========

	/**
	 * Check if geographic lighting is enabled
	 */
	get isEnabled(): boolean {
		return this.#enabled;
	}

	/**
	 * Get current location
	 */
	get location(): GeoLocation {
		return { ...this.#location };
	}

	/**
	 * Get current time setting
	 */
	get time(): Date | TimePreset | 'realtime' {
		return this.#time;
	}

	/**
	 * Get current weather preset
	 */
	get weather(): WeatherPreset {
		return this.#weather;
	}

	/**
	 * Get current state
	 */
	get state(): GeoLightingState {
		const currentTime = getCurrentTimeCfn(this);
		const sunPosition = this.calculateSunPosition(currentTime);
		const isNight = sunPosition.elevation < 0;
		const moonPosition = isNight ? this.calculateMoonPosition(currentTime) : null;

		return {
			config: {
				enabled: this.#enabled,
				location: this.#location,
				time: this.#time,
				weather: this.#weather
			},
			sunPosition,
			moonPosition,
			isNight,
			currentTime
		};
	}

	// ============ Public Methods ==========

	/**
	 * Enable geographic lighting with a location
	 */
	enable(location: GeoLocation): void {
		if (this.#enabled) {
			// Already enabled, just update location
			this.setLocation(location);
			return;
		}

		// Store original light states
		this.#originalLightState = storeOriginalLightStateCfn(this);

		this.#location = { ...location };
		this.#enabled = true;

		// Apply current settings
		updateLightingCfn(this);
	}

	/**
	 * Disable geographic lighting and restore original lights
	 */
	disable(): void {
		if (!this.#enabled) return;

		// Stop realtime updates
		this.stopRealtimeUpdates();

		// Restore original light states
		restoreOriginalLightStateCfn(this);

		this.#enabled = false;
	}

	/**
	 * Set geographic location
	 */
	setLocation(location: GeoLocation): void {
		this.#location = { ...location };
		if (this.#enabled) {
			updateLightingCfn(this);
		}
	}

	/**
	 * Set time (Date, TimePreset, or 'realtime')
	 */
	setTime(time: Date | TimePreset | 'realtime'): void {
		this.#time = time;

		if (time === 'realtime') {
			this.startRealtimeUpdates();
		} else {
			this.stopRealtimeUpdates();
		}

		if (this.#enabled) {
			updateLightingCfn(this);
		}
	}

	/**
	 * Set weather preset
	 */
	setWeather(weather: WeatherPreset): void {
		this.#weather = weather;
		if (this.#enabled) {
			updateLightingCfn(this);
		}
	}

	/**
	 * Set scene center (for light positioning)
	 */
	setSceneCenter(center: Vector3): void {
		this.#sceneCenter = center.clone();
		if (this.#enabled) {
			updateLightingCfn(this);
		}
	}

	/**
	 * Update lighting (call this each frame if using realtime)
	 */
	update(): void {
		if (this.#enabled && this.#time === 'realtime') {
			updateLightingCfn(this);
		}
	}

	/**
	 * Dispose resources
	 */
	dispose(): void {
		this.stopRealtimeUpdates();
		this.#enabled = false;
	}

	// ============ Sun/Moon Calculations ==========

	/**
	 * Calculate sun position using simplified solar equations
	 */
	calculateSunPosition(date: Date): CelestialPosition {
		return calculateSunPositionCfn(this, { date });
	}

	/**
	 * Calculate moon position (simplified - opposite of sun with offset)
	 */
	calculateMoonPosition(date: Date): CelestialPosition {
		return calculateMoonPositionCfn(this, { date });
	}

	// ============ Internal Accessors (for CFN files) ==========

	/** Access config */
	get configAccess(): GeoLightingManagerConfig {
		return this.#config;
	}

	/** Access original light state */
	get originalLightState(): OriginalLightState {
		return this.#originalLightState;
	}

	/** Access internal values */
	get internalAccess(): { sceneCenter: Vector3; lightDistance: number } {
		return {
			sceneCenter: this.#sceneCenter,
			lightDistance: this.#lightDistance
		};
	}

	// ============ Private Methods ==========

	/**
	 * Start realtime update interval
	 */
	private startRealtimeUpdates(): void {
		this.stopRealtimeUpdates();
		this.#realtimeInterval = startRealtimeUpdatesCfn(this);
	}

	/**
	 * Stop realtime update interval
	 */
	private stopRealtimeUpdates(): void {
		stopRealtimeUpdatesCfn(this.#realtimeInterval);
		this.#realtimeInterval = null;
	}
}
